import { useEffect } from "react";

/**
 * Calls `onClickOutside` on any mousedown whose target isn't inside `ref`.
 * The listener is always attached (not conditionally added/removed based on
 * an "is this open" flag) - callers that only care while something is open
 * should guard inside their own callback (`() => { if (isOpen) close(); }`).
 * An always-on listener with a cheap guard is more robust than attaching/
 * detaching it around open state: there's no window where a just-opened
 * dropdown's outside-click handling depends on an effect re-run landing
 * before the next native event fires.
 */
function useClickOutside(ref, onClickOutside) {
  useEffect(() => {
    function handleClickOutside(event) {
      // ModalShell (and anything else) portals to document.body, so a click
      // inside it never lands inside `ref` in the real DOM even when the
      // modal is a React child of whatever `ref` wraps - without this check,
      // opening such a modal from inside a click-outside-managed dropdown
      // (e.g. the org switcher) closes the dropdown, and with it the modal,
      // on the modal's very first mousedown.
      if (event.target.closest(".modal-shell__overlay")) return;
      if (ref.current && !ref.current.contains(event.target)) {
        onClickOutside();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, onClickOutside]);
}

export default useClickOutside;
